import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { loadArtworkTexture, artProxyUrl } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';

// Shared-rig contract (see the framing addendum): the swarm IS the light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'swarm',
  family: 'particles',
  danceability: 0.70,
  // 0, not 1. requiresTier gates SELECTION, not capability: this file has
  // had a real tier-0 layout all along (see its TIER table). The radio page
  // settles at tier 0 on the hardware it actually runs on - measured on
  // judicael.com - so at 1 this was never offered there at all, which is
  // why none of the recent visualizers ever appeared on the web radio. The
  // cost is that weak devices now get it at its tier-0 layout rather than
  // not at all; the frame-time governor still owns the tier itself.
  requiresTier: 0,
  requiresFloatTextures: true,
  energyLevel: 0.82,
  bpmMin: 100,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.70,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: FLOAT_TEXTURES_OK
};

// Swarm: 40 000 lit points that reorganise into shapes - the title, the cover
// in pointillism, geometric solids - then disperse. The reference is a drone
// show: thousands of points flying from one figure to the next, resolving
// progressively, never teleporting.
//
// THE ASSIGNMENT IS THE WHOLE VISUALIZER. When a new shape activates,
// something has to decide which particle flies to which target point. Random
// assignment gives 40 000 crossing trajectories and unreadable chaos for the
// whole transition. Sorting both sets by the same spatial key and pairing
// them index-for-index approximates optimal transport for the cost of two
// sorts: near particles get near targets and the formation reorganises in
// flight. ?swarmsort=0 turns it off, which is the demonstration.
//
// Positions and velocities live in float ping-pong textures (Silk's
// FeedbackBuffer), stepped at a fixed 1/60 with a capped speed - the speed
// cap is what produces formation FLIGHT rather than a jump.

const TIER = {
  // Tier 0 exists only as a live-downgrade refuge - the orchestrator never
  // offers swarm below tier 1 (requiresTier).
  0: { n: 128, shapes: ['title', 'artwork', 'sphere', 'ring', 'cloud'] },
  1: { n: 128, shapes: ['title', 'artwork', 'sphere', 'ring', 'cloud'] },
  2: { n: 200, shapes: ['title', 'artwork', 'sphere', 'torus', 'ring', 'helix', 'cloud'] }
};
const DEFAULT_TIER = 2;

const CAMERA_RADIUS = 9.0;
const CAMERA_HEIGHT = 0.6;
const ORBIT_PERIOD_S = 90;

// --- simulation ---------------------------------------------------------------
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 2;
const ATTRACT_BASE = 9.0;        // spring to target, scaled by bands.low
const ATTRACT_BAND_LO = 0.7;
const ATTRACT_BAND_HI = 1.3;
// 3.0, not 0.9. Against a damping of 3.4 a spring of 0.9 is heavily
// OVERDAMPED - time constant 3.8s - so with shapes changing every 8s the
// dispersed cloud never actually reached its own targets: measured, 78% of
// the swarm was still bunched in the middle of the frame, which is precisely
// what was happening behind the cover. At 3.0 it is near critically damped
// and settles in about 0.6s, while still being three times looser than a
// formation.
const ATTRACT_CLOUD = 3.0;
const DAMPING = 3.4;
const WANDER_FORM = 0.22;
const WANDER_CLOUD = 1.5;
const MAX_SPEED = 4.2;           // calibrated: see TRANSITION note below
// Containment, in the frame's own proportions. Beyond the widest shape (the
// cover square's corners reach 7.5) and the wide cloud, so nothing is
// distorted; tight enough vertically to hold strays inside the picture.
const BOUND_HALF = [9.4, 5.4, 4.0];
const CENTER_PULL = 3.0;         // strong, but only beyond BOUND_RADIUS
const STAGGER_SPAN_S = 1.2;      // the resolution wave, front to back
// A particle crosses at most ~8 units between shapes; at MAX_SPEED that is
// 1.9s, plus the 1.2s stagger span = a 2.5-3.5s transition, which is the
// brief's window. Measured end to end in the harness rather than assumed.

// --- shapes -------------------------------------------------------------------
// THE DOM OVERLAY IS PART OF THE COMPOSITION, not an obstacle to ignore.
// Measured at this framing it occupies +-2.91 x +-3.70 world units in the
// formation plane, so at the original SHAPE_SPAN of 4.6 it hid 80% of a
// planar shape's height - which is why so much was happening behind it.
// Everything below is sized against that measurement: the formations fill
// nearly the whole frame, and the dispersed state is a SHELL with a hole
// where the cover sits, so the motion happens around it.
// 5.3, not the 5.95 the frame would allow: the spring is underdamped
// (damping ratio 0.57), so a particle OVERSHOOTS its target by about 11% of
// its travel before settling. Measured at 5.85, that overshoot pushed 2.3%
// of the swarm past the frame edge. The headroom is the overshoot.
const SHAPE_SPAN = 5.3;
// THE DISPERSED CLOUD IS AN ELLIPSOIDAL SHELL, WIDE. A sphere of radius 4.6
// used almost none of the frame: 16:9 gives 10.8 world units of half-WIDTH
// against 6.07 of half-height, and the cover occupies the middle 2.91 of it.
// Measured, a spherical cloud left 63% of the swarm behind the cover however
// hard the keep-out pushed - the limit was geometric, not a matter of force.
// Spread sideways instead, most of the swarm is simply somewhere else.
//
// Z stays small on purpose: depth magnifies x and y in projection (a
// particle 3 units nearer the lens is 1.5x further out on screen), so a deep
// cloud throws its own edges out of frame.
const CLOUD_HALF = [7.0, 4.2, 2.0];
const CLOUD_INNER_FRAC = 0.45;
// Clear margin kept around the overlay when a shape is pushed off it.
const KEEPOUT_MARGIN = 1.0;
// Strong enough to beat the cloud's own wander, which is what refilled the
// hole when the keep-out lived only in the target positions.
// 40, not 9: the cloud's own spring pulls particles back toward shell targets
// that themselves fall inside the cylinder, and at 9 the two balanced out
// with 70% of the swarm still behind the cover. At 40 the push wins outright
// and the speed cap bounds how fast they leave, so it never looks violent.
const KEEP_PUSH = 40.0;
const ART_CANVAS = 220;
const TITLE_CANVAS_W = 640;
const TITLE_CANVAS_H = 260;

// --- render -------------------------------------------------------------------
const POINT_SIZE_NEAR = 5.0;
const POINT_SIZE_MIN = 2.0;
const POINT_SIZE_MAX = 5.0;
const INFLIGHT_BOOST = 1.4;      // particles still travelling are brighter
const INFLIGHT_DIST = 0.35;
// Measured on a full-coverage planar shape (the cover, which fills its
// square): the value at which 40 000 additive points on one camera-facing
// plane sit just under saturation.
const PLANAR_INTENSITY = 0.38;
const SAFE_FLOOR = 0.5;

// --- musical --------------------------------------------------------------------
const EXPLODE_IMPULSE = 26.0;
const EXPLODE_BEATS = 1;
const TIGHTEN_BEATS = 2;         // formation tightens before the drop
const FORM_MIN_S = 5.0;          // a shape holds at least this long
const NO_GRID_SHAPE_S = 8.0;     // fallback cadence when there is no bpm
// OUVRIR SUR UNE FORME PRECISE, après ce délai. Pour la page d'accueil, où un
// visuel ne dure que dix secondes : sans grille de tempo la première forme
// n'arrive qu'à la huitième (NO_GRID_SHAPE_S), et laquelle ce sera tient à un
// tirage - on regardait donc le nuage dispersé pendant huit secondes sur dix,
// et le titre n'apparaissait qu'une fois sur deux. Le délai n'est pas nul : le
// nuage d'abord, puis l'essaim qui s'organise, c'est cela qui est beau.
const OPEN_DELAY_S = 1.8;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function idle(fn) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 400 });
  else setTimeout(fn, 0);
}

// ============================================================================
// Simulation shaders - highp: positions integrate for minutes.
// ============================================================================
const simVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// A smooth swirling field, NOT a true curl noise: three incommensurate
// sine/cosine products, which is divergence-free enough to read as air and
// costs a fraction of a simplex-gradient curl. The brief's curlNoise() by
// intent, not by letter.
const CURL_CHUNK = `
  vec3 curlish(vec3 p) {
    return vec3(
      sin(p.y * 1.7) * cos(p.z * 1.3),
      sin(p.z * 1.1) * cos(p.x * 1.9),
      sin(p.x * 1.3) * cos(p.y * 1.5)
    );
  }
`;

const velocityFragmentShader = `
  precision highp float;

  uniform sampler2D uPos;
  uniform sampler2D uVel;
  uniform sampler2D uTargets;
  uniform float uDt;
  uniform float uTime;
  uniform float uShapeTime;
  uniform float uAttract;
  uniform float uDamping;
  uniform float uWander;
  uniform float uMaxSpeed;
  uniform float uCenterPull;
  uniform vec3 uBoundHalf;
  uniform vec2 uKeepHalf;    // the overlay's half-extents; zero = no keep-out
  uniform vec3 uKeepRight;   // camera right, world - the keep-out's own frame
  uniform float uKeepPush;
  uniform float uExplode;

  varying vec2 vUv;
  ${CURL_CHUNK}

  void main() {
    vec3 pos = texture2D(uPos, vUv).xyz;
    vec3 vel = texture2D(uVel, vUv).xyz;
    vec4 T = texture2D(uTargets, vUv);

    // The staggered start: every particle carries its own delay (see the
    // sorted assignment - the delay is its rank), so the formation resolves
    // as a wave sweeping through the swarm instead of snapping as one block.
    // 'active' is a reserved word in GLSL.
    float started = step(T.w, uShapeTime);

    vec3 accel = (T.xyz - pos) * (uAttract * started)
               - vel * uDamping
               + curlish(pos * 0.4 + uTime * 0.1) * uWander;

    float r = length(pos);
    // CONTAINMENT, not compression, and shaped like the FRAME rather than
    // like a ball: 16:9 has far more room sideways than vertically, and a
    // spherical bound big enough for the wide cloud would let strays out of
    // the top and bottom. Acts only outside the ellipsoid, so no formation
    // is distorted by it.
    vec3 q = pos / uBoundHalf;
    float qd = length(q);
    if (qd > 1.0) {
      vec3 g2 = pos / (uBoundHalf * uBoundHalf);
      float gl2 = length(g2);
      accel -= (gl2 > 1e-4 ? g2 / gl2 : vec3(0.0)) * ((qd - 1.0) * uCenterPull * 8.0);
    }

    // THE COVER'S RESERVED SPACE, as a force rather than just as target
    // positions: in the dispersed state the wander (1.5) beats the loose
    // cloud spring (0.9) and simply refills any hole left in the targets -
    // measured, the shell rendered as a solid ball. Pushing out of it is what
    // actually keeps the drift AROUND the cover.
    //
    // An elliptical CYLINDER along the view axis - the cover's own screen
    // footprint, extruded in depth. It is not a sphere (the cover is far
    // taller than it is wide, so a sphere clearing it would waste the frame)
    // and it is not symmetric about Y either: that was tried first and left
    // 76% of the swarm behind the cover, because the camera orbits
    // HORIZONTALLY - a particle far out along the view direction has a large
    // radius about Y and still projects dead centre. The frame is handed in
    // as uKeepRight, so it tracks the orbit.
    if (uKeepHalf.x > 0.01) {
      float px = dot(pos, uKeepRight);
      vec2 e = vec2(px / uKeepHalf.x, pos.y / uKeepHalf.y);
      float inside = length(e);
      if (inside < 1.0) {
        vec2 g = vec2(px / (uKeepHalf.x * uKeepHalf.x), pos.y / (uKeepHalf.y * uKeepHalf.y));
        float gl = length(g);
        vec2 dir = gl > 1e-4 ? g / gl : vec2(0.0, 1.0);
        accel += (uKeepRight * dir.x + vec3(0.0, 1.0, 0.0) * dir.y)
               * ((1.0 - inside) * uKeepPush);
      }
    }

    // Radial impulse on a drop.
    accel += (r > 0.001 ? pos / r : vec3(0.0, 1.0, 0.0)) * uExplode;

    vel += accel * uDt;
    // THE SPEED CAP is what makes this fly in formation rather than jump:
    // without it the spring term alone moves a distant particle most of the
    // way in one frame.
    float sp = length(vel);
    if (sp > uMaxSpeed) vel *= uMaxSpeed / sp;

    gl_FragColor = vec4(vel, 0.0);
  }
`;

const positionFragmentShader = `
  precision highp float;
  uniform sampler2D uPos;
  uniform sampler2D uVel;
  uniform float uDt;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uPos, vUv).xyz + texture2D(uVel, vUv).xyz * uDt, 1.0);
  }
`;

// ============================================================================
// Render
// ============================================================================
const pointsVertexShader = `
  precision mediump float;

  uniform sampler2D uPosTex;
  uniform sampler2D uTargets;
  uniform sampler2D uColorTex;
  uniform float uSizeNear;
  uniform float uPixelRatio;
  uniform float uArtMix;
  uniform vec3 uShapeColor;
  uniform float uBeatPhase;
  uniform float uStaggerSpan;
  uniform float uSparkle;
  uniform float uIntensity;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec2 tUv = position.xy;
    vec4 P = texture2D(uPosTex, tUv);
    vec4 T = texture2D(uTargets, tUv);

    vec4 mv = modelViewMatrix * vec4(P.xyz, 1.0);
    gl_Position = projectionMatrix * mv;

    float depth = max(-mv.z, 0.001);
    gl_PointSize = clamp(uSizeNear / depth, ${POINT_SIZE_MIN.toFixed(1)}, ${POINT_SIZE_MAX.toFixed(1)}) * uPixelRatio;

    // Colour: the sampled cover pixel in ARTWORK, the shape's palette colour
    // otherwise, with a per-particle jitter so the mass never reads as flat.
    vec3 art = texture2D(uColorTex, tUv).rgb;
    float jitter = fract(sin(dot(tUv, vec2(41.7, 289.1))) * 43758.5453);
    vec3 base = mix(uShapeColor * (0.82 + 0.36 * jitter), art, uArtMix);

    // In flight = brighter. This is what makes the reorganisation readable:
    // the moving particles are the ones you see.
    float dist = length(T.xyz - P.xyz);
    float inflight = smoothstep(0.05, ${INFLIGHT_DIST.toFixed(2)}, dist);
    float bright = mix(1.0, ${INFLIGHT_BOOST.toFixed(2)}, inflight);

    // The beat wave: one band of light per beat, travelling through the sort
    // order (T.w IS the rank, scaled). Not a global pulse.
    float rank = uStaggerSpan > 0.0001 ? T.w / uStaggerSpan : 0.0;
    float d = fract(uBeatPhase - rank + 1.0);
    bright += exp(-d * d * 90.0) * 0.9;
    bright += uSparkle * jitter;

    // Aerial attenuation, so the solids read as volumes.
    float atmo = clamp(1.0 - (depth - 5.0) * 0.055, 0.35, 1.0);

    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float safe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));

    vColor = base * bright * atmo * uIntensity;
    vAlpha = safe;
  }
`;

const pointsFragmentShader = `
  precision mediump float;
  uniform float uArtMix;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    // Near-white core, very light halo - the density makes the image, not the
    // size of the dots.
    vec2 d = gl_PointCoord - 0.5;
    float r2 = dot(d, d);
    if (r2 > 0.25) discard;
    float core = 1.0 - smoothstep(0.0, 0.055, r2);
    float halo = 1.0 - smoothstep(0.0, 0.25, r2);
    // The core is near-white for the palette shapes, where that IS the look -
    // but the cover shape lives or dies on its real colours, and 40 000
    // overlapping near-white cores washed the sunset to grey. So the
    // whiteness backs off exactly as the artwork colours fade in.
    float whiteness = mix(0.55, 0.15, uArtMix);
    vec3 col = mix(vColor * halo * 0.55, mix(vColor, vec3(1.0), whiteness), core);
    // AdditiveBlending is (SRC_ALPHA, ONE): all the intensity in rgb.
    gl_FragColor = vec4(col * vAlpha, 1.0);
  }
`;

const texDebugFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uTex, vUv).xyz * 0.12 + 0.5, 1.0);
  }
`;

export class SwarmVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const forced = urlParam('swarmshape');
    this._forcedShape = forced && TIER[this._tier].shapes.indexOf(forced) !== -1 ? forced : null;
    // La forme sur laquelle OUVRIR, une seule fois, avant de reprendre le
    // cycle normal. À ne pas confondre avec _forcedShape, qui la VERROUILLE.
    const open = options && options.openOn;
    this._openOn = open && TIER[this._tier].shapes.indexOf(open) !== -1 ? open : null;
    this._sortEnabled = urlParam('swarmsort') !== '0';
    this._texDebug = urlParam('swarmtex') === '1';
    this._explodeDebug = urlParam('swarmexplode') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._n = layout.n;
    this._count = this._n * this._n;
    this._shapeNames = layout.shapes.slice();

    // Planar shapes live in a container that tracks the camera, so they face
    // it by construction while the camera orbits; solids leave it at
    // identity and reveal their volume. The container SLERPS between the two,
    // so switching between a plane and a solid rotates the swarm into place
    // instead of snapping it.
    this._container = new THREE.Group();
    scene.add(this._container);

    const bufOpts = { width: this._n, height: this._n, type: THREE.FloatType, filter: THREE.NearestFilter };
    this._pos = new FeedbackBuffer(bufOpts);
    this._vel = new FeedbackBuffer(bufOpts);
    this._simReady = false;

    // Targets: xyz = the point this particle flies to, w = its start delay.
    // Rewritten only on a shape change, never per frame.
    this._targetData = new Float32Array(this._count * 4);
    this._targetTex = new THREE.DataTexture(this._targetData, this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    this._targetTex.minFilter = THREE.NearestFilter;
    this._targetTex.magFilter = THREE.NearestFilter;
    this._targetTex.needsUpdate = true;

    this._colorData = new Uint8Array(this._count * 4);
    this._colorTex = new THREE.DataTexture(this._colorData, this._n, this._n, THREE.RGBAFormat);
    this._colorTex.minFilter = THREE.NearestFilter;
    this._colorTex.magFilter = THREE.NearestFilter;
    this._colorTex.needsUpdate = true;

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial());
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);

    this._velUniforms = {
      uPos: { value: null }, uVel: { value: null }, uTargets: { value: this._targetTex },
      uDt: { value: FIXED_DT }, uTime: { value: 0 }, uShapeTime: { value: 0 },
      uAttract: { value: ATTRACT_CLOUD }, uDamping: { value: DAMPING },
      uWander: { value: WANDER_CLOUD }, uMaxSpeed: { value: MAX_SPEED },
      uCenterPull: { value: CENTER_PULL },
      uBoundHalf: { value: new THREE.Vector3(BOUND_HALF[0], BOUND_HALF[1], BOUND_HALF[2]) },
      uKeepHalf: { value: new THREE.Vector2(0, 0) }, uKeepPush: { value: KEEP_PUSH },
      uKeepRight: { value: new THREE.Vector3(1, 0, 0) },
      uExplode: { value: 0 }
    };
    this._posUniforms = { uPos: { value: null }, uVel: { value: null }, uDt: { value: FIXED_DT } };
    this._velMaterial = new THREE.ShaderMaterial({
      uniforms: this._velUniforms, vertexShader: simVertexShader,
      fragmentShader: velocityFragmentShader, depthTest: false, depthWrite: false
    });
    this._posMaterial = new THREE.ShaderMaterial({
      uniforms: this._posUniforms, vertexShader: simVertexShader,
      fragmentShader: positionFragmentShader, depthTest: false, depthWrite: false
    });

    // --- the points ---
    const geo = new THREE.BufferGeometry();
    const uvs = new Float32Array(this._count * 3);
    for (let y = 0; y < this._n; y++) {
      for (let x = 0; x < this._n; x++) {
        const i = (y * this._n + x) * 3;
        uvs[i] = (x + 0.5) / this._n;
        uvs[i + 1] = (y + 0.5) / this._n;
        uvs[i + 2] = 0;
      }
    }
    geo.setAttribute('position', new THREE.BufferAttribute(uvs, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 100);

    this._pointUniforms = Object.assign({}, safeZoneUniforms, {
      uPosTex: { value: null },
      uTargets: { value: this._targetTex },
      uColorTex: { value: this._colorTex },
      uSizeNear: { value: POINT_SIZE_NEAR * CAMERA_RADIUS },
      uPixelRatio: { value: 1 },
      uArtMix: { value: 0 },
      uShapeColor: { value: new THREE.Color(0x6fd0ff) },
      uBeatPhase: { value: 0 },
      uStaggerSpan: { value: STAGGER_SPAN_S },
      uSparkle: { value: 0 },
      uIntensity: { value: 1 }
    });
    this._pointsMat = new THREE.ShaderMaterial({
      uniforms: this._pointUniforms,
      vertexShader: pointsVertexShader,
      fragmentShader: pointsFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._points = new THREE.Points(geo, this._pointsMat);
    this._points.frustumCulled = false;
    const self = this;
    this._points.onBeforeRender = function (renderer) { self._runSim(renderer); };
    this._container.add(this._points);

    if (this._texDebug) {
      this._debugUniforms = { uTex: { value: null } };
      this._debugMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
        uniforms: this._debugUniforms, vertexShader: simVertexShader,
        fragmentShader: texDebugFragmentShader, depthTest: false, depthWrite: false
      }));
      this._debugMesh.frustumCulled = false;
      this._debugMesh.renderOrder = 1000;
      scene.add(this._debugMesh);
    }

    // --- shape store ---
    this._shapes = {};
    // Per-shape render intensity, keyed by name - see _activateShape.
    this._shapeIntensity = { sphere: 1.0, torus: 1.0, helix: 1.0, cloud: 1.0, ring: PLANAR_INTENSITY };
    this._artColors = null;
    this._generateStaticShapes();

    // --- state ---
    this._time = 0;
    this._shapeTime = 0;
    this._accumulator = 0;
    this._paused = false;
    this._currentShape = null;
    this._currentTargets = null;     // the sort reference - see _assign
    this._cycleIndex = 0;
    // Which of the two cover-derived formations opens a cycle. See
    // _pickFormation: it used to be title, always, so every cycle of every
    // track read the same way round.
    this._titleFirst = Math.random() < 0.5;
    this._solidIndex = 0;
    this._lastPhraseIndex = null;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._lastPaletteRef = undefined;
    this._palette = [new THREE.Color(0x6fd0ff), new THREE.Color(0xff6fb0), new THREE.Color(0xffd86f)];
    this._lowSmooth = 0.5;
    this._highSmooth = 0.5;
    this._explodeUntil = -1;
    this._tightenUntil = -1;
    this._pendingDropAt = null;
    this._orbitAngle = 0;
    this._artMixTarget = 0;
    this._intensityTarget = 1;
    this._formationBias = 0;

    this._sortA = new Float64Array(this._count);
    this._sortB = new Float64Array(this._count);

    this._activateShape('cloud');
    this._placeCamera(0);
  }

  // ---------------------------------------------------------------- shapes --
  _generateStaticShapes() {
    const N = this._count;
    const want = this._shapeNames;
    if (want.indexOf('sphere') !== -1) this._shapes.sphere = this._makeSphere(N);
    if (want.indexOf('torus') !== -1) this._shapes.torus = this._makeTorus(N);
    if (want.indexOf('ring') !== -1) this._shapes.ring = this._makeRing(N);
    if (want.indexOf('helix') !== -1) this._shapes.helix = this._makeHelix(N);
    this._shapes.cloud = this._makeCloud(N);
  }

  _makeSphere(N) {
    // Fibonacci lattice - the standard even distribution on a sphere.
    const out = new Float32Array(N * 3);
    const ga = Math.PI * (3 - Math.sqrt(5));
    const R = 4.3;
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const th = ga * i;
      out[i * 3] = Math.cos(th) * r * R;
      out[i * 3 + 1] = y * R;
      out[i * 3 + 2] = Math.sin(th) * r * R;
    }
    return out;
  }

  _makeTorus(N) {
    // Uniform ON THE SURFACE, not in the parameter square: the area element
    // is (R + r cos v), so v is drawn by rejection against it. Sampling v
    // uniformly would crowd the inner rim.
    const out = new Float32Array(N * 3);
    const R = 3.6, r = 1.35;
    for (let i = 0; i < N; i++) {
      const u = Math.random() * Math.PI * 2;
      let v = 0;
      for (let t = 0; t < 24; t++) {
        v = Math.random() * Math.PI * 2;
        if (Math.random() * (R + r) <= R + r * Math.cos(v)) break;
      }
      out[i * 3] = (R + r * Math.cos(v)) * Math.cos(u);
      out[i * 3 + 1] = r * Math.sin(v);
      out[i * 3 + 2] = (R + r * Math.cos(v)) * Math.sin(u);
    }
    return out;
  }

  _makeRing(N) {
    const out = new Float32Array(N * 3);
    const r0 = 3.7, r1 = 5.2;
    for (let i = 0; i < N; i++) {
      const th = Math.random() * Math.PI * 2;
      // sqrt keeps the area density even across the annulus
      const rr = Math.sqrt(lerp(r0 * r0, r1 * r1, Math.random()));
      out[i * 3] = Math.cos(th) * rr;
      out[i * 3 + 1] = Math.sin(th) * rr;
      out[i * 3 + 2] = (Math.random() - 0.5) * 0.25;
    }
    return out;
  }

  _makeHelix(N) {
    const out = new Float32Array(N * 3);
    // Radius 4.6, not 2.9: at 2.9 the whole helix fitted inside the cover's
    // own half-width of 2.91 and was therefore entirely hidden behind it -
    // measured, 75% of the swarm. Height stays the binding constraint on a
    // 16:9 frame (at H = 9.2, 2.3% of the swarm cleared the top and bottom).
    // CIRCULAR, and sized by two constraints at once. Wider than the cover's
    // own half-width (2.91) or it hides behind it entirely - at R = 2.9 it
    // did, 75% of the swarm. Circular rather than flattened, because the
    // camera orbits: a ribbon wide in x is edge-on half the time, and a
    // flattened helix measured 83% hidden from the wrong angle. Compact
    // enough that depth magnification (1.7x at this radius) keeps the top and
    // bottom of a 6.4-tall helix inside the frame.
    const R = 3.6, H = 6.4, TURNS = 3.0;
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      const strand = (i % 2) * Math.PI;
      const th = t * TURNS * Math.PI * 2 + strand;
      out[i * 3] = Math.cos(th) * R + (Math.random() - 0.5) * 0.12;
      out[i * 3 + 1] = (t - 0.5) * H + (Math.random() - 0.5) * 0.12;
      out[i * 3 + 2] = Math.sin(th) * R + (Math.random() - 0.5) * 0.12;
    }
    return out;
  }

  // A SHELL, not a ball: the dispersed state keeps clear of the middle so the
  // drift reads around the cover instead of behind it.
  _makeCloud(N) {
    const out = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      let x, y, z, d;
      do {
        x = Math.random() * 2 - 1; y = Math.random() * 2 - 1; z = Math.random() * 2 - 1;
        d = x * x + y * y + z * z;
      } while (d > 1 || d < 1e-6);
      // Even by volume between the two shell radii, then stretched to the
      // ellipsoid.
      const u = Math.random();
      const i3 = Math.pow(CLOUD_INNER_FRAC, 3);
      const t = Math.cbrt(i3 + u * (1 - i3)) / Math.sqrt(d);
      out[i * 3] = x * t * CLOUD_HALF[0];
      out[i * 3 + 1] = y * t * CLOUD_HALF[1];
      out[i * 3 + 2] = z * t * CLOUD_HALF[2];
    }
    return out;
  }

  // The overlay's half-extents, in world units in the formation plane. Read
  // LIVE from the measured safe zone, so a longer title that wraps to a
  // second line moves the keep-out with it.
  _overlayWorld() {
    const fov = (this._camera && this._camera.fov ? this._camera.fov : 68) * Math.PI / 180;
    const halfH = CAMERA_RADIUS * Math.tan(fov / 2);
    // R19 : la présence se lit sur la pochette, la bande et l'évitement sur le
    // centre de scène (là où serait la pochette centrée).
    const shs = safeZoneUniforms.uSafeHalfSize.value;
    const sc = safeZoneUniforms.uSafeCenter.value;
    const hs = safeZoneUniforms.uStageHalfSize.value;
    const c = safeZoneUniforms.uStageCenter.value;
    // PAS D'INCRUSTATION DU TOUT. scene-manager gare la boîte très loin hors
    // cadre quand la page n'a pas d'élément #overlay - la page d'accueil, la
    // démo, la TV - et quand un visualiseur la masque. Lue telle quelle, une
    // boîte garée à deux cent soixante-dix-huit unités de haut déplaçait la
    // bande « au-dessus de l'incrustation » loin hors de l'écran, et le titre
    // s'y formait invisible : on ne voyait plus que la queue du nuage.
    //
    // Il n'y a alors rien à éviter, et la formation reprend tout le cadre.
    if (!(shs.x > 0) || !(shs.y > 0) || Math.abs(sc.y) > 1.5) {
      return { hx: 0, hy: 0, cy: -halfH, frameHalf: halfH, absente: true };
    }
    return { hx: hs.x * halfH, hy: hs.y * halfH, cy: c.y * halfH, frameHalf: halfH };
  }

  // Fits a point set into the clear band ABOVE the overlay, preserving its
  // aspect. Used by TITLE, which the user was seeing appear behind the cover.
  _fitAboveOverlay(pts, N) {
    const ov = this._overlayWorld();
    let mnx = 1e9, mxx = -1e9, mny = 1e9, mxy = -1e9;
    for (let i = 0; i < N; i++) {
      mnx = Math.min(mnx, pts[i * 3]); mxx = Math.max(mxx, pts[i * 3]);
      mny = Math.min(mny, pts[i * 3 + 1]); mxy = Math.max(mxy, pts[i * 3 + 1]);
    }
    const w = Math.max(0.001, mxx - mnx), h = Math.max(0.001, mxy - mny);
    // Sans incrustation, la bande est TOUT le cadre et le titre s'y centre;
    // avec, elle commence au-dessus d'elle comme avant.
    const bandBottom = ov.absente ? -ov.frameHalf * 0.90 : ov.cy + ov.hy * KEEPOUT_MARGIN;
    const bandTop = ov.frameHalf * 0.90;   // same overshoot headroom
    const bandH = Math.max(0.5, bandTop - bandBottom);
    const bandW = SHAPE_SPAN * 2;
    const k = Math.min(bandW / w, bandH / h);
    const cx = (mnx + mxx) / 2, cy = (mny + mxy) / 2;
    const targetY = (bandBottom + bandTop) / 2;
    for (let i = 0; i < N; i++) {
      pts[i * 3] = (pts[i * 3] - cx) * k;
      pts[i * 3 + 1] = (pts[i * 3 + 1] - cy) * k + targetY;
    }
  }

  // Points sampled from an inked canvas. Two passes rather than blind
  // rejection: collect the candidate pixels once, then draw from that list.
  // Blind rejection on a title (a few percent inked) would spend most of its
  // tries on empty pixels.
  _sampleCanvas(ctx, w, h, N, weighted, colorsOut) {
    const img = ctx.getImageData(0, 0, w, h).data;
    const idx = [];
    const cum = weighted ? [] : null;
    let total = 0;
    for (let i = 0; i < w * h; i++) {
      const a = img[i * 4 + 3];
      if (a < 24) continue;
      const lum = (0.299 * img[i * 4] + 0.587 * img[i * 4 + 1] + 0.114 * img[i * 4 + 2]) / 255;
      if (weighted) {
        // Density PROPORTIONAL TO LUMINANCE: bright areas get more points.
        // lum^1.4, not lum^2: squared weighting piled so many points into the
        // highlights that they saturated before the shape was even lit.
        const wgt = 0.04 + Math.pow(lum, 1.4);
        total += wgt;
        idx.push(i); cum.push(total);
      } else {
        if (lum < 0.35) continue;
        idx.push(i);
      }
    }
    if (idx.length === 0) return null;
    // How much of the canvas this shape actually occupies - the density the
    // 40 000 points will land at.
    this._lastCoverage = idx.length / (w * h);

    const out = new Float32Array(N * 3);
    const aspect = w / h;
    // STRATIFIED inverse-CDF: the N draws are generated already increasing
    // ((k + rand)/N), so the CDF is walked ONCE with a single pointer instead
    // of N binary searches - 88k steps instead of 624k, which is what keeps
    // the whole generation inside one idle callback. Stratification also
    // spreads the points more evenly than independent draws, which is
    // exactly what pointillism wants.
    let walk = 0;
    for (let k = 0; k < N; k++) {
      let pix;
      if (weighted) {
        const r = ((k + Math.random()) / N) * total;
        while (walk < cum.length - 1 && cum[walk] < r) walk++;
        pix = idx[walk];
      } else {
        pix = idx[(Math.random() * idx.length) | 0];
      }
      const px = pix % w, py = (pix / w) | 0;
      // Jitter inside the pixel, or the swarm lands on a visible lattice.
      const fx = (px + Math.random()) / w - 0.5;
      const fy = 0.5 - (py + Math.random()) / h;
      out[k * 3] = fx * SHAPE_SPAN * 2 * (aspect >= 1 ? 1 : aspect);
      out[k * 3 + 1] = fy * SHAPE_SPAN * 2 * (aspect >= 1 ? 1 / aspect : 1);
      out[k * 3 + 2] = (Math.random() - 0.5) * 0.10;
      if (colorsOut) {
        colorsOut[k * 4] = img[pix * 4];
        colorsOut[k * 4 + 1] = img[pix * 4 + 1];
        colorsOut[k * 4 + 2] = img[pix * 4 + 2];
        colorsOut[k * 4 + 3] = 255;
      }
    }
    return out;
  }

  _generateTitleShape(meta) {
    const text = (cleanDisplayTitle(meta.title) || cleanDisplayArtist(meta.artist)
      || (meta.album || '')).trim();
    if (!text) { this._shapes.title = null; return; }
    const cv = document.createElement('canvas');
    cv.width = TITLE_CANVAS_W; cv.height = TITLE_CANVAS_H;
    const x = cv.getContext('2d', { willReadFrequently: true });
    x.clearRect(0, 0, cv.width, cv.height);
    x.fillStyle = '#fff';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    let size = 190;
    const words = text.split(' ');
    let lines = [text];
    if (words.length > 1) {
      // Two lines when one would have to shrink too far.
      x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`;
      if (x.measureText(text).width > cv.width * 0.92) {
        let best = 0, bestD = 1e9;
        for (let i = 1; i < words.length; i++) {
          const d = Math.abs(words.slice(0, i).join(' ').length - text.length / 2);
          if (d < bestD) { bestD = d; best = i; }
        }
        lines = [words.slice(0, best).join(' '), words.slice(best).join(' ')];
      }
    }
    for (;;) {
      x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`;
      let wide = 0;
      for (const l of lines) wide = Math.max(wide, x.measureText(l).width);
      if ((wide <= cv.width * 0.92 && size * lines.length * 1.05 <= cv.height * 0.9) || size <= 28) break;
      size -= 6;
    }
    const lh = size * 1.02;
    const y0 = cv.height / 2 - (lines.length - 1) * lh / 2;
    for (let i = 0; i < lines.length; i++) x.fillText(lines[i], cv.width / 2, y0 + i * lh);
    const pts = this._sampleCanvas(x, cv.width, cv.height, this._count, false, null);
    if (pts) {
      this._shapeIntensity.title = Math.max(0.05, PLANAR_INTENSITY * this._lastCoverage);
      // Into the clear band above the overlay - a fixed 12% lift was not
      // enough against an overlay 61% of the frame tall, which is why the
      // title kept appearing behind the cover.
      this._fitAboveOverlay(pts, this._count);
    }
    this._shapes.title = pts;
  }

  _generateArtworkShape(image) {
    const cv = document.createElement('canvas');
    cv.width = ART_CANVAS; cv.height = ART_CANVAS;
    const x = cv.getContext('2d', { willReadFrequently: true });
    try {
      x.drawImage(image, 0, 0, ART_CANVAS, ART_CANVAS);
    } catch (e) {
      this._shapes.artwork = null;
      return;
    }
    if (!this._artColors) this._artColors = new Uint8Array(this._count * 4);
    const pts = this._sampleCanvas(x, cv.width, cv.height, this._count, true, this._artColors);
    this._shapeIntensity.artwork = Math.max(0.05, PLANAR_INTENSITY * this._lastCoverage);
    // The colours are NOT uploaded here: the colour of particle p is the
    // colour of the target point p was ASSIGNED, and that permutation is only
    // known in _assign. Uploading them in generation order would paint every
    // particle with a stranger's pixel.
    this._shapes.artwork = pts;
  }

  // ------------------------------------------------------------ assignment --
  // The sorted assignment. Both sets are sorted by the same spatial key
  // (angle about the centre, then radius) and paired index for index.
  //
  // The particles are sorted by their CURRENT TARGETS rather than by reading
  // their positions back from the GPU. A 200x200 RGBA32F readback is 640KB
  // and stalls the pipeline - exactly the frame spike criterion 7 forbids -
  // and by the time a shape changes (a phrase apart, at least FORM_MIN_S) the
  // formation has settled onto those targets, so they ARE where the particles
  // are. In the loose cloud state the ordering is still broadly preserved,
  // which is all the key needs.
  _assign(points, colors) {
    const N = this._count;
    const ref = this._currentTargets;
    const A = this._sortA, B = this._sortB;

    // Key packed into one float64 so the sort runs as a native numeric sort
    // on a typed array - no comparator callback, which is what keeps two
    // 40k sorts down to a couple of milliseconds. The key half is an exact
    // integer multiple of 1e5, so the index survives the round trip.
    const key = function (x, y, z) {
      let a = Math.atan2(y, x) / (Math.PI * 2);
      if (a < 0) a += 1;
      const d = Math.min(1, Math.sqrt(x * x + y * y + z * z) / 8);
      return Math.floor(a * 1e6) * 1000 + Math.floor(d * 999);
    };

    for (let i = 0; i < N; i++) {
      if (ref) A[i] = key(ref[i * 3], ref[i * 3 + 1], ref[i * 3 + 2]) * 1e5 + i;
      else A[i] = i * 1e5 + i;
      B[i] = key(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]) * 1e5 + i;
    }
    if (this._sortEnabled) { A.sort(); B.sort(); }

    const td = this._targetData;
    const stagger = STAGGER_SPAN_S / N;
    for (let k = 0; k < N; k++) {
      const pi = A[k] % 1e5;          // particle
      const ti = B[k] % 1e5;          // target point
      const o = pi * 4;
      td[o] = points[ti * 3];
      td[o + 1] = points[ti * 3 + 1];
      td[o + 2] = points[ti * 3 + 2];
      // The rank IS the delay: the resolution sweeps through the swarm in
      // sort order, which is angular - it reads as a wave going round.
      td[o + 3] = k * stagger;
      if (colors) {
        const co = pi * 4, so = ti * 4;
        this._colorData[co] = colors[so];
        this._colorData[co + 1] = colors[so + 1];
        this._colorData[co + 2] = colors[so + 2];
        this._colorData[co + 3] = 255;
      }
    }
    // One upload for the whole assignment.
    this._targetTex.needsUpdate = true;
    if (colors) this._colorTex.needsUpdate = true;

    // Remember where the formation now is, as the next sort's reference.
    if (!this._currentTargets) this._currentTargets = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const o = i * 4;
      this._currentTargets[i * 3] = td[o];
      this._currentTargets[i * 3 + 1] = td[o + 1];
      this._currentTargets[i * 3 + 2] = td[o + 2];
    }
  }

  // Rend true quand la forme a bien été activée, false quand elle n'existe
  // pas encore - ce que l'ouverture ci-dessus utilise pour réessayer.
  _activateShape(name) {
    const pts = this._shapes[name];
    if (!pts) return false;
    this._assign(pts, name === 'artwork' ? this._artColors : null);
    this._currentShape = name;
    this._shapeTime = 0;
    this._artMixTarget = name === 'artwork' ? 1 : 0;
    // DENSITY AND COLOUR BOTH CARRY TONE, so brightness compounds. A planar
    // shape puts all 40 000 points on one camera-facing plane - the overlap
    // is total and additive - while a solid spreads them over a volume seen
    // edge-on. Measured, the flat shapes blew out to white at the same
    // intensity that left the solids correct, so the planar ones run dimmer.
    // Intensity follows the shape's own COVERAGE, measured at generation.
    // All 40 000 points land on whatever the shape occupies: the cover fills
    // its square, but a title inks about 8% of its canvas, so the same points
    // pile up ~12x denser. Brightness per unit area is N*I/A, so holding it
    // constant across shapes means I scales LINEARLY with coverage - a square
    // root was tried first and barely moved the title off saturation.
    this._intensityTarget = this._shapeIntensity[name] != null
      ? this._shapeIntensity[name] : 1.0;
    if (name !== 'artwork' && name !== 'cloud') {
      this._pointUniforms.uShapeColor.value.copy(this._palette[this._cycleIndex % this._palette.length]);
    }
    return true;
  }

  // The cycle: cloud between every real shape, so each formation is born out
  // of dispersion. artwork comes round twice as often as the solids - it is
  // the strongest of them.
  _nextShape() {
    const solids = this._shapeNames.filter(function (s) {
      return s !== 'cloud' && s !== 'title' && s !== 'artwork';
    });
    // progress -> the PROPORTION OF TIME spent in formation rather than in
    // cloud, peaking mid-track: the higher the bias, the more often the
    // cloud step between two shapes is skipped outright. Expressed as time,
    // which is what the brief asks for, rather than as a spring gain.
    if (this._currentShape !== 'cloud') {
      return Math.random() < this._formationBias ? this._pickFormation(solids) : 'cloud';
    }
    return this._pickFormation(solids);
  }

  // The two cover-derived formations - the title and the artwork - used to sit
  // in that fixed order, so the title was always the first thing the swarm
  // built and the cover always the second. Which one opens the cycle now flips
  // every time round, and is re-rolled on a track change, so neither a single
  // track nor a whole sitting settles into one rhythm.
  //
  // The flip is decided at the TOP of a cycle and the order array is otherwise
  // rebuilt identically on each of the four calls, so the sequence a cycle
  // actually plays cannot change halfway through it.
  _pickFormation(solids) {
    const step = this._cycleIndex % 4;
    if (step === 0) this._titleFirst = !this._titleFirst;
    const first = this._titleFirst ? 'title' : 'artwork';
    const second = this._titleFirst ? 'artwork' : 'title';
    // The doubled slot is the cycle's OPENER, not artwork specifically. Two
    // reasons: it keeps the pair even over time now that which one leads
    // alternates, and it is what stops the same cover formation landing back to
    // back - the next cycle opens with the OTHER one, so ending on this one is
    // always safe. Ending on a fixed 'artwork' gave artwork twice running on
    // every cycle that artwork opened: measured, 3 times in 8 cycles.
    //
    // The solid gets its OWN counter. _cycleIndex advances by four per cycle,
    // so indexing four solids by it landed on the same one for ever - the tier
    // lists seven shapes and only three of them were ever built.
    const solid = solids.length ? solids[this._solidIndex % solids.length] : 'cloud';
    const order = [first, second, solid, first];
    const pick = order[step];
    this._cycleIndex++;
    if (step === 3) this._solidIndex++;
    return (pick && this._shapes[pick]) ? pick : 'cloud';
  }

  // ------------------------------------------------------------- simulation --
  _seed(renderer) {
    const N = this._count;
    const pos = new Float32Array(N * 4);
    const src = this._shapes.cloud;
    for (let i = 0; i < N; i++) {
      pos[i * 4] = src[i * 3];
      pos[i * 4 + 1] = src[i * 3 + 1];
      pos[i * 4 + 2] = src[i * 3 + 2];
      pos[i * 4 + 3] = 1;
    }
    const tex = new THREE.DataTexture(pos, this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const copy = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: tex } }, vertexShader: simVertexShader,
      fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main(){ gl_FragColor = texture2D(uTex, vUv); }',
      depthTest: false, depthWrite: false
    });
    this._simQuad.material = copy;
    for (const rt of [this._pos.read, this._pos.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    const zero = new THREE.DataTexture(new Float32Array(N * 4), this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    zero.needsUpdate = true;
    copy.uniforms.uTex.value = zero;
    for (const rt of [this._vel.read, this._vel.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    copy.dispose(); tex.dispose(); zero.dispose();
  }

  _substep(renderer) {
    // velocity: (pos, vel, targets) -> vel.write
    this._simQuad.material = this._velMaterial;
    this._velUniforms.uPos.value = this._pos.read.texture;
    this._velUniforms.uVel.value = this._vel.read.texture;
    renderer.setRenderTarget(this._vel.write);
    renderer.render(this._simScene, this._ortho);
    this._vel.swap();

    // position: (pos, NEW vel) -> pos.write
    this._simQuad.material = this._posMaterial;
    this._posUniforms.uPos.value = this._pos.read.texture;
    this._posUniforms.uVel.value = this._vel.read.texture;
    renderer.setRenderTarget(this._pos.write);
    renderer.render(this._simScene, this._ortho);
    this._pos.swap();
  }

  _runSim(renderer) {
    if (!this._simReady) {
      this._pos.ensureSize(renderer);
      this._vel.ensureSize(renderer);
      this._seed(renderer);
      this._simReady = true;
      this._pointUniforms.uPixelRatio.value = renderer.getPixelRatio();
    }
    if (this._paused) return;

    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;

    let steps = 0;
    while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
      this._velUniforms.uTime.value = this._time - this._accumulator;
      this._velUniforms.uShapeTime.value = this._shapeTime - this._accumulator;
      this._substep(renderer);
      this._accumulator -= FIXED_DT;
      steps++;
    }
    // Surplus beyond the substep budget is DROPPED, not integrated: a
    // variable dt would make the swarm behave differently at 30 and 60 fps.
    if (this._accumulator > FIXED_DT * MAX_SUBSTEPS) this._accumulator = FIXED_DT * MAX_SUBSTEPS;

    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    this._pointUniforms.uPosTex.value = this._pos.read.texture;
    if (this._debugUniforms) this._debugUniforms.uTex.value = this._pos.read.texture;
  }

  _placeCamera(dt) {
    this._orbitAngle += (dt / ORBIT_PERIOD_S) * Math.PI * 2;
    const c = this._camera;
    c.position.set(
      Math.sin(this._orbitAngle) * CAMERA_RADIUS,
      CAMERA_HEIGHT,
      Math.cos(this._orbitAngle) * CAMERA_RADIUS
    );
    c.lookAt(0, 0, 0);
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;     // freezes the simulation AND the orbit
    this._time += deltaTime;
    this._shapeTime += deltaTime;
    this._accumulator += deltaTime;
    this._placeCamera(deltaTime);

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / 0.18);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 120;
    const beatS = 60 / bpm;
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._formationBias = 0.55 * Math.sin(Math.PI * progress);

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
      }
    }

    // Track change: regenerate the two cover-derived shapes, off the hot path.
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      // A fresh coin for the new track, so two tracks in a row do not open the
      // same way just because their cycles happened to line up.
      this._titleFirst = Math.random() < 0.5;
      // The strings are captured NOW: scene-manager mutates one profile
      // object in place, so by the time the idle callback runs it could
      // already describe the next track.
      const self = this;
      const meta = {
        title: profile.title, artist: profile.artist, album: profile.album
      };
      idle(function () { self._generateTitleShape(meta); });
    }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () { idle(function () { self._generateArtworkShape(img); }); };
        img.onerror = function () { self._shapes.artwork = null; };
        img.src = artProxyUrl(profile.artworkUrl);
      } else {
        this._shapes.artwork = null;
      }
    }

    // --- shape cycle, on phrase downbeats ---
    const phraseIndex = profile.phraseIndex;
    if (this._forcedShape) {
      if (this._currentShape !== this._forcedShape && this._shapes[this._forcedShape]) {
        this._activateShape(this._forcedShape);
      }
    } else if (this._openOn && this._shapeTime > OPEN_DELAY_S
               && this._activateShape(this._openOn)) {
      // L'OUVERTURE PASSE AVANT LA BRANCHE DES PHRASES, et l'activation est
      // DANS la condition. Les deux comptent.
      //
      // Avant : la branche `phraseIndex != null` l'interceptait. Or sur la
      // page d'accueil phraseIndex vaut 0 - un nombre, pas null - et il ne
      // bouge jamais, puisqu'un visuel n'y dure que dix secondes. Mesuré, sur
      // cent vingt images : un seul phraseIndex, toujours 0. La branche était
      // donc prise et ne déclenchait rien, et le repli au chronomètre en
      // dessous était inatteignable : l'essaim restait en nuage du début à la
      // fin, sans jamais former quoi que ce soit.
      //
      // Et l'activation dans la condition : si la forme n'est pas encore
      // prête - le titre attend que la piste soit annoncée, la pochette
      // qu'elle soit chargée - la condition est fausse et on RETOMBE sur les
      // branches suivantes, au lieu de les priver.
      this._openOn = null;
    } else if (phraseIndex != null) {
      if (this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex
        && this._shapeTime > FORM_MIN_S) {
        this._activateShape(this._nextShape());
      }
    } else if (this._shapeTime > NO_GRID_SHAPE_S) {
      // NO BEAT GRID, NO STANDING STILL. Shapes normally change on phrase
      // downbeats, and phraseIndex is null whenever the bpm is unknown - on
      // a radio stream, or for the first seconds of any track. Left as it
      // was, the swarm sat in the dispersed cloud for the whole track and
      // never formed anything at all, which reads as "nothing is happening".
      // A plain timer takes over: the same cycle, just not musically placed.
      this._activateShape(this._nextShape());
    }
    this._lastPhraseIndex = phraseIndex;

    // --- drops: tighten two beats before, explode ON the drop ---
    // director.nextEvent carries only {type, score, msUntil} - no absolute
    // timestamp, and it switches to the FOLLOWING mark the instant one
    // passes, so "msUntil <= 0" never happens and cannot be the trigger.
    // The drop's arrival is latched in this visualizer's own clock instead:
    // once the mark is inside the tighten window, its due time is known, and
    // the explosion fires when the clock reaches it - exactly ON the drop,
    // preceded by the tightening.
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise')) {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until <= TIGHTEN_BEATS * beatS) {
        this._pendingDropAt = this._time + until;
        this._tightenUntil = this._pendingDropAt;
      }
    }
    if (this._pendingDropAt != null && this._time >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._explodeUntil = this._time + EXPLODE_BEATS * beatS;
    }
    if (this._explodeDebug) { this._explodeDebug = false; this._explodeUntil = this._time + EXPLODE_BEATS * beatS; }
    const exploding = this._time < this._explodeUntil;
    const tightening = !exploding && this._time < this._tightenUntil;

    // --- the simulation's knobs ---
    const inCloud = this._currentShape === 'cloud';
    let attract = (inCloud ? ATTRACT_CLOUD : ATTRACT_BASE)
      * lerp(ATTRACT_BAND_LO, ATTRACT_BAND_HI, this._lowSmooth);
    if (tightening) attract *= 1.8;
    if (exploding) attract = 0;
    this._velUniforms.uAttract.value = attract;
    this._velUniforms.uWander.value = inCloud
      ? WANDER_CLOUD * (0.6 + 0.9 * this._highSmooth)
      : WANDER_FORM;
    this._velUniforms.uExplode.value = exploding ? EXPLODE_IMPULSE : 0;
    // ONLY in the dispersed state. The cover-derived shapes are pictures -
    // carving a hole through one would destroy the very thing the DOM cover
    // sits in the middle of - and the solids are recognisable objects whose
    // silhouette a keep-out would wreck. The cloud is where the visible
    // MOTION lives, which is what the request was about.
    const keepOut = inCloud;
    if (keepOut && !exploding) {
      const ov = this._overlayWorld();
      this._velUniforms.uKeepHalf.value.set(ov.hx * KEEPOUT_MARGIN, ov.hy * KEEPOUT_MARGIN);
      const e = this._camera.matrixWorld.elements;
      this._velUniforms.uKeepRight.value.set(e[0], e[1], e[2]).normalize();
    } else {
      this._velUniforms.uKeepHalf.value.set(0, 0);
    }
    this._velUniforms.uCenterPull.value = CENTER_PULL;

    // --- render uniforms ---
    // Without a grid the light wave runs on the clock rather than freezing on
    // a constant, so the formation still breathes.
    this._pointUniforms.uBeatPhase.value = profile.beatPhase != null
      ? profile.beatPhase : (this._time * (bpm / 60)) % 1;
    this._pointUniforms.uSparkle.value = inCloud ? 0 : this._highSmooth * 0.45;
    const am = this._pointUniforms.uArtMix;
    am.value += (this._artMixTarget - am.value) * Math.min(1, deltaTime * 2.2);
    const iu = this._pointUniforms.uIntensity;
    iu.value += ((this._intensityTarget != null ? this._intensityTarget : 1) - iu.value)
      * Math.min(1, deltaTime * 2.2);

    // Planar shapes face the camera; solids stay in world axes. Slerped, so
    // the switch rotates the swarm rather than snapping it.
    const planar = this._currentShape === 'title' || this._currentShape === 'artwork'
      || this._currentShape === 'ring';
    const want = planar ? this._camera.quaternion : _identityQuat;
    this._container.quaternion.slerp(want, Math.min(1, deltaTime * 1.1));
  }

  dispose() {
    this._scene.remove(this._container);
    if (this._debugMesh) {
      this._scene.remove(this._debugMesh);
      this._debugMesh.geometry.dispose();
      this._debugMesh.material.dispose();
    }
    this._points.geometry.dispose();
    this._pointsMat.dispose();
    this._simQuad.geometry.dispose();
    this._velMaterial.dispose();
    this._posMaterial.dispose();
    this._pos.dispose();
    this._vel.dispose();
    this._targetTex.dispose();
    this._colorTex.dispose();
  }
}

const _identityQuat = new THREE.Quaternion();
